import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Stack,
  Link as MUILink,
  Checkbox,
  FormControlLabel,
  Alert,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Visibility, VisibilityOff } from "@mui/icons-material";

function AuthDrawer({
  open,
  onClose,
  onLogin,
  onSignup,
  onRequestPasswordReset,
  onResendConfirmation,
}) {
  // Controls which tab (login / signup / reset) we show
  const [mode, setMode] = useState("login");

  // ------------------------------------------------------------
  // 1. LOGIN STATES
  // ------------------------------------------------------------
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // ------------------------------------------------------------
  // 2. SIGNUP STATES
  // ------------------------------------------------------------
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupError, setSignupError] = useState(null);
  const [isSignupSubmitting, setIsSignupSubmitting] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  // NEW: track whether sign-up succeeded
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Helper to clear sign-up fields
  const resetSignupFields = () => {
    setFirstName("");
    setLastName("");
    setSignupEmail("");
    setSignupPassword("");
    setSignupError(null);
    setSignupSuccess(false);
  };

  // For a simple check if password < 8 chars
  const isPasswordShort = signupPassword.length < 8;

  // ------------------------------------------------------------
  // 3. RESET (FORGOT PASSWORD) STATES
  // ------------------------------------------------------------
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState(null);
  const [resetSuccess, setResetSuccess] = useState(null);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);

  // ------------------------------------------------------------
  // 4. RESEND CONFIRMATION STATES
  // ------------------------------------------------------------
  const [resendConfirmSuccess, setResendConfirmSuccess] = useState(null);
  const [resendConfirmError, setResendConfirmError] = useState(null);

  // ============================================================
  // LOGIN SUBMIT HANDLER
  // ============================================================
  const handleLoginSubmit = async () => {
    try {
      setLoginError(null);
      setIsLoginSubmitting(true);

      await onLogin(loginEmail, loginPassword);
      onClose();
    } catch (error) {
      console.error("Login failed:", error);

      if (
        error.response?.status === 403 &&
        error.response?.data?.message
          ?.toLowerCase()
          .includes("confirm your email")
      ) {
        // a 403 with "confirm your email" => user not confirmed
        setLoginError("Please confirm your email first");
      } else {
        setLoginError(
          "Unauthorized: We cannot sign you in with the provided information."
        );
      }
    } finally {
      setIsLoginSubmitting(false);
    }
  };

  // ============================================================
  // SIGNUP SUBMIT HANDLER
  // ============================================================
  const handleSignupSubmit = async () => {
    try {
      setSignupError(null);

      // front-end enforcement
      if (isPasswordShort) {
        setSignupError("Password must be at least 8 characters.");
        return;
      }

      setIsSignupSubmitting(true);

      // Attempt to sign up
      await onSignup(
        firstName,
        lastName,
        signupEmail,
        signupPassword
      );

      // If success => show success screen
      setSignupSuccess(true);

      // (We do NOT call onClose() here, because we want user
      //  to see the "Check your email" success message.)
    } catch (error) {
      console.error("Signup failed:", error);
      setSignupError("Sign Up Failed. Please try again.");
    } finally {
      setIsSignupSubmitting(false);
    }
  };

  // ============================================================
  // FORGOT PASSWORD
  // ============================================================
  const handleRequestResetSubmit = async () => {
    if (!onRequestPasswordReset) {
      console.log("No onRequestPasswordReset provided");
      return;
    }
    try {
      setIsResetSubmitting(true);
      setResetError(null);
      setResetSuccess(null);

      const msg = await onRequestPasswordReset(resetEmail);
      setResetSuccess(
        msg || "If we have an account with that email, a reset link was sent."
      );
    } catch (error) {
      console.error("Password Reset request failed:", error);
      setResetError(
        "Could not process your request. Check your email or try again later."
      );
    } finally {
      setIsResetSubmitting(false);
    }
  };

  // ============================================================
  // RESEND CONFIRMATION
  // ============================================================
  const handleResendConfirmation = async (email) => {
    if (!onResendConfirmation) return;
    try {
      setResendConfirmSuccess(null);
      setResendConfirmError(null);

      const message = await onResendConfirmation(email);
      setResendConfirmSuccess(
        message || `A new confirmation email has been sent to ${email}`
      );
    } catch (error) {
      console.error("Resend confirmation failed:", error);
      setResendConfirmError(
        error.response?.data?.message ||
          "Failed to resend confirmation. Please try again."
      );
    }
  };

  // ============================================================
  // MODE SWITCHERS
  // ============================================================
  // Whenever user clicks "Sign Up" link from login
  // we reset the sign-up fields so user sees blank form
  const switchToSignup = () => {
    resetSignupFields();
    setMode("signup");
  };

  // If user is on sign-up success screen, but wants to sign in
  const handleReturnToSignIn = () => {
    setSignupSuccess(false);
    resetSignupFields();
    setMode("login");
  };

  // The main UI
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: {
            xs: "100%",
            sm: "50%",
            md: "33.333%",
          },
        },
      }}
    >
      <Box sx={{ p: 2, height: "100%", position: "relative" }}>
        {/* Close Icon */}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 8, right: 8 }}
          aria-label="Close drawer"
        >
          <CloseIcon />
        </IconButton>

        {/* Title depends on mode */}
        <Typography variant="h6" align="center" sx={{ mb: 2, mt: 3 }}>
          {mode === "login"
            ? "Sign In"
            : mode === "signup"
            ? "Sign Up"
            : "Forgot Password?"}
        </Typography>

        {/* =========================
            LOGIN FORM
        ========================== */}
        {mode === "login" && (
          <Box sx={{ mt: 2 }}>
            <Stack
              spacing={2}
              sx={{
                width: { xs: "90%", sm: "80%", md: "80%" },
                margin: "0 auto",
              }}
            >
              {loginError && (
                <Alert severity="error" onClose={() => setLoginError(null)}>
                  {loginError}
                </Alert>
              )}
              {/* If a resend success or error occurred */}
              {resendConfirmSuccess && (
                <Alert
                  severity="success"
                  onClose={() => setResendConfirmSuccess(null)}
                >
                  {resendConfirmSuccess}
                </Alert>
              )}
              {resendConfirmError && (
                <Alert
                  severity="error"
                  onClose={() => setResendConfirmError(null)}
                >
                  {resendConfirmError}
                </Alert>
              )}

              <TextField
                label="Email *"
                variant="outlined"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                fullWidth
              />

              {/* Show/hide password */}
              <TextField
                label="Password *"
                variant="outlined"
                type={showLoginPassword ? "text" : "password"}
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() =>
                          setShowLoginPassword((prev) => !prev)
                        }
                        edge="end"
                      >
                        {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={keepMeSignedIn}
                    onChange={(e) => setKeepMeSignedIn(e.target.checked)}
                  />
                }
                label="Keep me signed in"
              />

              <Typography variant="body2">
                By clicking <strong>Sign In</strong> you accept our{" "}
                <MUILink href="/terms" color="secondary">
                  Terms and Conditions
                </MUILink>
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleLoginSubmit}
                  sx={{ textTransform: "none" }}
                  disabled={
                    isLoginSubmitting ||
                    !loginEmail.trim() ||
                    !loginPassword.trim()
                  }
                >
                  {isLoginSubmitting ? "Signing in..." : "Sign In"}
                </Button>
                <MUILink
                  component="button"
                  underline="hover"
                  sx={{ color: "secondary.main", alignSelf: "center" }}
                  onClick={() => setMode("reset")}
                >
                  Forgot/Reset password?
                </MUILink>
              </Box>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="body2" component="span">
                  Not Registered?{" "}
                </Typography>
                <MUILink
                  component="button"
                  variant="body2"
                  color="secondary"
                  onClick={switchToSignup}
                  sx={{ fontWeight: "bold" }}
                >
                  Sign Up
                </MUILink>
              </Box>

              {/* If the loginError is specifically about unconfirmed email,
                  show a "Resend Confirmation" link.  */}
              {loginError && loginError.includes("confirm your email") && (
                <Box sx={{ textAlign: "center", mt: 1 }}>
                  <MUILink
                    component="button"
                    variant="body2"
                    onClick={() => handleResendConfirmation(loginEmail)}
                    color="secondary"
                  >
                    Resend Confirmation?
                  </MUILink>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        {/* =========================
            SIGNUP FORM
        ========================== */}
        {mode === "signup" && (
          <Box sx={{ mt: 2 }}>
            {!signupSuccess ? (
              /* If not yet successful, show sign-up form */
              <Stack
                spacing={2}
                sx={{
                  width: { xs: "90%", sm: "80%", md: "80%" },
                  margin: "0 auto",
                }}
              >
                {signupError && (
                  <Alert
                    severity="error"
                    onClose={() => setSignupError(null)}
                  >
                    {signupError}
                  </Alert>
                )}

                <TextField
                  label="First Name *"
                  variant="outlined"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Last Name *"
                  variant="outlined"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Email *"
                  variant="outlined"
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Password *"
                  variant="outlined"
                  type={showSignupPassword ? "text" : "password"}
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  fullWidth
                  error={isPasswordShort && signupPassword.length > 0}
                  helperText={
                    isPasswordShort && signupPassword.length > 0
                      ? "Password must be at least 8 characters"
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowSignupPassword((prev) => !prev)
                          }
                          edge="end"
                        >
                          {showSignupPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: "flex", justifyContent: "start" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSignupSubmit}
                    sx={{ textTransform: "none" }}
                    disabled={
                      isSignupSubmitting ||
                      isPasswordShort ||
                      !firstName.trim() ||
                      !lastName.trim() ||
                      !signupEmail.trim() ||
                      !signupPassword.trim()
                    }
                  >
                    {isSignupSubmitting ? "Signing up..." : "Sign Up"}
                  </Button>
                </Box>
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography variant="body2" component="span">
                    Already Registered?{" "}
                  </Typography>
                  <MUILink
                    component="button"
                    variant="body2"
                    color="secondary"
                    onClick={() => {
                      // user wants to go back to login
                      // also reset sign-up form
                      setSignupSuccess(false);
                      resetSignupFields();
                      setMode("login");
                    }}
                    sx={{ fontWeight: "bold" }}
                  >
                    Sign In
                  </MUILink>
                </Box>
              </Stack>
            ) : (
              /* If signupSuccess = true => show success screen */
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Alert severity="success">
                  Your account has been created! Please check your email to confirm.
                </Alert>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  We’ve sent a confirmation link to your email address.
                </Typography>

                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  sx={{ mt: 3 }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleReturnToSignIn}
                  >
                    Return to Sign In
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                  >
                    Return to Home
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* =========================
            RESET (FORGOT PASSWORD) FORM
        ========================== */}
        {mode === "reset" && (
          <Box sx={{ mt: 2 }}>
            {!resetSuccess ? (
              <Stack
                spacing={2}
                sx={{
                  width: { xs: "90%", sm: "80%", md: "80%" },
                  margin: "0 auto",
                }}
              >
                {resetError && (
                  <Alert severity="error" onClose={() => setResetError(null)}>
                    {resetError}
                  </Alert>
                )}

                <TextField
                  label="Enter your account email address"
                  variant="outlined"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  fullWidth
                />
                <Typography variant="body2" color="text.secondary">
                  If you have an active account with us using this email address,
                  we will send you an email with a link to set a new password.
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "start" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRequestResetSubmit}
                    sx={{ textTransform: "none" }}
                    disabled={isResetSubmitting || !resetEmail.trim()}
                  >
                    {isResetSubmitting ? "Sending..." : "Send"}
                  </Button>
                </Box>
              </Stack>
            ) : (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Alert severity="success">{resetSuccess}</Alert>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Please check your email for further instructions!
                </Typography>

                <Button
                  variant="outlined"
                  sx={{ mt: 3 }}
                  onClick={() => setMode("login")}
                >
                  Back to Login
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default AuthDrawer;
